import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import styled from 'styled-components';

import { Separator, FlexView } from '@cuemath/comps';
import { Header, Grid } from 'semantic-ui-react';
import {
  post as createTeacher,
  invalidateList as invalidateTeacherList,
} from 'actions/teacher-list';
import { patch as editTeacher } from 'actions/teachers';
import { USER_SELECT_DISABLE_ARRAY } from 'constants/constants';
import {
  HeaderHead,
  Footer,
  Message,
  FormWrapper,
  ButtonCenterWrapper,
  CenterButton,
  renderFormField,
  getOptions,
} from 'modules/form';

import {
  FIELD_TYPES,
  teacherFields,
  getOnChangeMethod,
  getInputChangeMethod,
} from 'constants/form';

const HeaderHeadWrapper = styled(HeaderHead)`
  padding: 10px 10px 0px 10px;
`;

class TeacherBasicForm extends PureComponent {
  static defaultProps = {
    teacher: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      isValid: true,
      errorText: null,
      message: null,
      teacherPatch: store.patch(),
      form: this.getFormDefaults(),
      formFields: this.getFormFields(),
      updateTeacher: false,
    };
    teacherFields.forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });
  }

  static propTypes = {
    actions: PropTypes.shape({
      createTeacher: PropTypes.func.isRequired,
      editTeacher: PropTypes.func.isRequired,
      invalidateTeacherList: PropTypes.func.isRequired,
    }).isRequired,
    teacher: PropTypes.object,
    userConfig: PropTypes.object.isRequired,
    programs: PropTypes.array.isRequired,
  };

  getFormDefaults = () => {
    const { teacher } = this.props;
    const formDefaults = {};

    teacherFields.forEach(field => {
      formDefaults[field.name] =
        !!teacher && teacher[field.param] ? teacher[field.param] : field.default;
    });
    return formDefaults;
  };

  getFormFields = () => {
    const { userConfig, programs } = this.props;

    return teacherFields.map(field => {
      if (field.fieldType === FIELD_TYPES.SELECT)
        field.choices = getOptions({ field: field.name, userConfig, programs });
      return field;
    });
  };

  static getDerivedStateFromProps(props, state) {
    const { teachers, actions } = props;
    const { teacherPatch } = state;
    const newTeacherPatch = store.patch(teachers, teacherPatch.id);
    const newState = {};

    // handle patch action
    if (newTeacherPatch.resolved) {
      if (newTeacherPatch.resolved.isPatched) {
        newState.teacherPatch = newTeacherPatch;
        newState.teacherData = newTeacherPatch.resolved.data;
        newState.message = 'Teacher details edited successfully';
        actions.invalidateTeacherList();
      } else if (newTeacherPatch.resolved.isPatchingFailed) {
        newState.teacherPatch = newTeacherPatch;
        newState.errorText = store.getError(newTeacherPatch.resolved).message;
      }
      newState.updateTeacher = false;
    }

    return newState;
  }

  componentDidUpdate() {
    const { teacher, teacherSlots, actions } = this.props;
    const {
      updateTeacher,
      teacherPatch: { id },
      form,
      formFields,
    } = this.state;

    if (teacher && teacher.id && updateTeacher) {
      const teacherSlotsGet = store.get(teacherSlots, teacher.id);
      if (teacherSlotsGet && teacherSlotsGet.isLoaded) {
        const data = teacherSlotsGet.data;
        if (data && Array.isArray(data) && data.length > 0) {
          this.setState({
            errorText: 'Expire Teacher Slots First',
            updateTeacher: false,
          });
        } else {
          this.setState(
            {
              updateTeacher: false,
            },
            () => {
              const data = {};
              formFields.forEach(({ name, param }) => {
                if (param && teacher[param] !== form[name]) data[param] = form[name];
              });
              actions.editTeacher(id, teacher.id, data);
            },
          );
        }
      }
    }
  }

  onChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;

    this.setState(() => ({ ...stateChanges }));
  };

  onToggle = event => {
    const { form, errorText } = this.state;
    const key = event.target.name;
    const value = event.target.checked;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onOptionChange = (key, value) => {
    const { form, errorText } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };
    if (errorText) stateChanges.errorText = null;
    this.setState(() => ({ ...stateChanges }));
  };

  handlePatchAction = () => {
    const {
      teacherPatch: { id },
      form,
      formFields,
    } = this.state;
    const { actions, teacher } = this.props;
    const data = {};

    formFields.forEach(({ name, param }) => {
      if (param && teacher[param] !== form[name]) data[param] = form[name];
    });

    if (Object.keys(data).length) {
      actions.editTeacher(id, teacher.id, data);
    }
  };

  onSubmit = event => {
    event.preventDefault();
    this.handlePatchAction();
  };

  renderFormHeader = () => {
    const { teacher } = this.props;
    if (teacher) {
      return (
        <HeaderHeadWrapper>
          <Header as="h5" floated="right">
            <a
              href={`/teacher-analytics/${teacher.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              See Teacher Analytics
            </a>
          </Header>

          <Header as="h3" floated="left">
            Basic Info
          </Header>
        </HeaderHeadWrapper>
      );
    } else {
      return '';
    }
  };

  renderFormFields = () => {
    const { formFields, form } = this.state;

    return (
      <FlexView>
        <Grid>
          {formFields.map(field => {
            const disabled = field.disabled || !field.editable;
            const required = false;
            const placeholder = disabled ? '' : field.label;

            return (
              <Grid.Column mobile={16} tablet={16} computer={8}>
                {renderFormField({
                  ...field,
                  disableUserSelect: USER_SELECT_DISABLE_ARRAY.includes(
                    field.name.toLowerCase(),
                  ),
                  profileView: true,
                  value: form[field.name],
                  onChange: this[getInputChangeMethod(field)],
                  required,
                  disabled,
                  placeholder,
                })}
              </Grid.Column>
            );
          })}
        </Grid>
      </FlexView>
    );
  };

  renderFormFooter = () => {
    return (
      <Footer>
        <ButtonCenterWrapper>
          <CenterButton label="SAVE" />
        </ButtonCenterWrapper>
      </Footer>
    );
  };

  render() {
    const { errorText, message } = this.state;

    return (
      <form onSubmit={this.onSubmit}>
        <FormWrapper>
          {this.renderFormHeader()}
          {message && <Message>{message}</Message>}
          <Separator transparent height="25" />
          {this.renderFormFields()}
          {errorText && <Message error={true}>{errorText}</Message>}
          <Separator transparent height="50" />
          {this.renderFormFooter()}
          <Separator transparent height="50" />
        </FormWrapper>
      </form>
    );
  }
}

export default connect(
  ({ teacherList, teachers, teacherSlots }) => ({ teacherList, teachers, teacherSlots }),
  dispatch => ({
    actions: bindActionCreators(
      {
        createTeacher,
        editTeacher,
        invalidateTeacherList,
      },
      dispatch,
    ),
  }),
)(TeacherBasicForm);
